import { useSelector } from 'react-redux';
import type { GreenroomState as GRS } from '../stores/types/types';
import type { Shot } from '../stores/types/shots';

type ShotLike = Pick<Shot, 'shot_id'> & { shot_data: Pick<Shot['shot_data'], 'shotDuration'> };

/**
 * Pure function — use inside hooks/callbacks that already select useSegments and distributedShotDurations.
 * Returns the shot's effective playback duration: either the manual shotDuration or the segment-distributed
 * duration for flexible shots (when use_segments is enabled and the shot has no manual duration).
 */
export function getEffectiveShotDuration(
	shot: ShotLike | null | undefined,
	useSegments: boolean,
	distributedShotDurations: Record<string, number>,
): number {
	if (!shot) return 0;
	const { shotDuration } = shot.shot_data;
	return (useSegments && !shotDuration)
		? (distributedShotDurations[shot.shot_id] ?? 0)
		: (shotDuration ?? 0);
}

/**
 * Hook wrapper around getEffectiveShotDuration — selects useSegments and distributedShotDurations
 * from the store automatically.
 */
export function useEffectiveShotDuration(shot: ShotLike | null | undefined): number {
	const useSegments = useSelector((s: GRS) => s.SettingsReducer.featureFlags.use_segments);
	const distributedShotDurations = useSelector((s: GRS) => s.SegmentsReducer.distributedShotDurations);
	return getEffectiveShotDuration(shot, useSegments, distributedShotDurations);
}
